import React, { useMemo, useCallback } from 'react';
import { TabState, createModelId } from '../../../../types';
import { LinearRegressionParameters, GLMParameters } from '../../../../types';
import { ModelManager } from '../../../utils/ModelManager';
import { useProjectData } from '../../../stores/hooks/useProjectData';
import { usePaneLayout } from '../../../stores/hooks/usePaneLayout';
import { TabRegistry } from '../../../tab-system/TabRegistry';
import logger from '../../../utils/logger';
import * as styles from './ModelDetailTabContent.css.ts';

interface ModelDetailTabContentProps {
  tab: TabState;
}

export const ModelDetailTabContent: React.FC<ModelDetailTabContentProps> = ({ tab }) => {
  const { project } = useProjectData();
  const { addTab, getActiveContainer } = usePaneLayout();

  // タブのメタデータからモデルIDを取得
  const modelId = tab.metadata?.modelId;

  // モデルの詳細を取得
  const model = useMemo(() => {
    if (!project || !modelId) {
      logger.warn('[ModelDetailTabContent] Cannot get model:', {
        hasProject: !!project,
        modelId
      });
      return null;
    }

    const modelManager = new ModelManager(project);
    const availableModelIds = Object.keys(project.models || {});
    logger.info('[ModelDetailTabContent] Looking up model:', {
      modelId,
      availableModelIds,
      totalModels: availableModelIds.length
    });

    const foundModel = modelManager.getModel(modelId);
    if (!foundModel) {
      logger.error('[ModelDetailTabContent] Model not found:', {
        modelId,
        availableModelIds
      });
    } else {
      logger.info('[ModelDetailTabContent] Model found:', {
        modelId,
        modelName: foundModel.name,
        modelType: foundModel.type
      });
    }
    return foundModel;
  }, [project, modelId]);

  // データセット名を取得
  const datasetName = useMemo(() => {
    if (!project || !model) return null;
    const dataset = Object.values(project.datasets).find(d => d.id === model.metadata.trainingDatasetId);
    return dataset?.name || model.metadata.trainingDatasetId;
  }, [project, model]);

  // GLM Diagnosticsタブを開く（React Hooksのルールに従い、early returnの前に定義）
  const openDiagnostics = useCallback(() => {
    if (!project || !modelId || !model) return;

    if (model.type !== 'glm') {
      logger.warn('[ModelDetailTabContent] Cannot open diagnostics for non-GLM model:', { modelType: model.type });
      return;
    }

    const diagnosticsTab = TabRegistry['glm-diagnostics'].createTab(
      createModelId(modelId),
      `GLM Diagnostics: ${model.name}`
    );

    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, diagnosticsTab, { activatePane: true });
      logger.info('[ModelDetailTabContent] Opened GLM Diagnostics tab:', { modelId, modelName: model.name });
    }
  }, [project, modelId, model, addTab, getActiveContainer]);

  // GLM Predictionタブを開く
  const openPrediction = useCallback(() => {
    if (!project || !modelId || !model) return;

    if (model.type !== 'glm') {
      logger.warn('[ModelDetailTabContent] Cannot open prediction for non-GLM model:', { modelType: model.type });
      return;
    }

    const predictionTab = TabRegistry['glm-prediction'].createTab(
      createModelId(modelId),
      `Prediction: ${model.name}`
    );

    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, predictionTab, { activatePane: true });
      logger.info('[ModelDetailTabContent] Opened GLM Prediction tab:', { modelId, modelName: model.name });
    }
  }, [project, modelId, model, addTab, getActiveContainer]);

  // 列名を取得するヘルパー関数
  const getColumnName = (columnId: string): string => {
    if (!project || !model) return columnId;
    const dataset = Object.values(project.datasets).find(d => d.id === model.metadata.trainingDatasetId);
    if (!dataset) return columnId;
    const column = dataset.schema.columns.find(c => c.id === columnId);
    return column?.name || columnId;
  };

  if (!model) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyMessage}>
          Model not found
        </div>
      </div>
    );
  }

  // 日付をフォーマット
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // パラメータを見やすく表示
  const renderParameters = () => {
    if (model.type === 'linear_regression') {
      const params = model.parameters as LinearRegressionParameters;
      return (
        <div className={styles.parameterSection}>
          <h3>Model Parameters</h3>

          {params.intercept !== undefined && (
            <div className={styles.parameterItem}>
              <span className={styles.parameterLabel}>Intercept:</span>
              <span className={styles.parameterValue}>{params.intercept.toFixed(6)}</span>
            </div>
          )}

          <div className={styles.coefficientsSection}>
            <h4>Coefficients</h4>
            <table className={styles.coefficientsTable}>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Coefficient</th>
                  <th>95% Confidence Interval</th>
                </tr>
              </thead>
              <tbody>
                {model.metadata.features.map((feature, index) => {
                  const coefIndex = params.intercept !== undefined ? index + 1 : index;
                  const coefficient = params.coefficients[coefIndex];
                  const ci = params.confidenceIntervals?.[coefIndex];

                  return (
                    <tr key={feature}>
                      <td>{getColumnName(feature)}</td>
                      <td className={styles.numeric}>{coefficient?.toFixed(6) || 'N/A'}</td>
                      <td className={styles.numeric}>
                        {ci ? `[${ci.lower.toFixed(6)}, ${ci.upper.toFixed(6)}]` : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (model.type === 'glm') {
      const params = model.parameters as GLMParameters;
      return (
        <div className={styles.parameterSection}>
          <h3>Model Parameters</h3>

          <div className={styles.parameterItem}>
            <span className={styles.parameterLabel}>Family:</span>
            <span className={styles.parameterValue}>{params.family}</span>
          </div>
          <div className={styles.parameterItem}>
            <span className={styles.parameterLabel}>Link:</span>
            <span className={styles.parameterValue}>{params.link}</span>
          </div>

          {params.intercept !== undefined && (
            <div className={styles.parameterItem}>
              <span className={styles.parameterLabel}>Intercept:</span>
              <span className={styles.parameterValue}>{params.intercept.toFixed(6)}</span>
            </div>
          )}

          <div className={styles.coefficientsSection}>
            <h4>Coefficients</h4>
            <table className={styles.coefficientsTable}>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Estimate</th>
                  <th>Std. Error</th>
                  <th>z value</th>
                  <th>Pr(&gt;|z|)</th>
                  <th>95% CI</th>
                </tr>
              </thead>
              <tbody>
                {model.metadata.features.map((feature, index) => {
                  const coefIndex = params.intercept !== undefined ? index + 1 : index;
                  const coefficient = params.coefficients[coefIndex];
                  const se = params.standardErrors[coefIndex];
                  const z = params.zValues[coefIndex];
                  const p = params.pValues[coefIndex];
                  const ci = params.confidenceIntervals?.[coefIndex];

                  return (
                    <tr key={feature}>
                      <td>{getColumnName(feature)}</td>
                      <td className={styles.numeric}>{coefficient?.toFixed(6) || 'N/A'}</td>
                      <td className={styles.numeric}>{se?.toFixed(6) || 'N/A'}</td>
                      <td className={styles.numeric}>{z?.toFixed(3) || 'N/A'}</td>
                      <td className={styles.numeric}>{p !== undefined ? (p < 0.001 ? '<0.001' : p.toFixed(4)) : 'N/A'}</td>
                      <td className={styles.numeric}>
                        {ci ? `[${ci.lower.toFixed(4)}, ${ci.upper.toFixed(4)}]` : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.fitStatistics}>
            <h4>Model Fit</h4>
            {params.theta !== undefined && (
              <div className={styles.parameterItem}>
                <span className={styles.parameterLabel}>Dispersion Parameter (θ):</span>
                <span className={styles.parameterValue}>{params.theta.toFixed(4)}</span>
              </div>
            )}
            <div className={styles.parameterItem}>
              <span className={styles.parameterLabel}>Null Deviance:</span>
              <span className={styles.parameterValue}>{params.nullDeviance.toFixed(2)} (df={params.nullDegreesOfFreedom})</span>
            </div>
            <div className={styles.parameterItem}>
              <span className={styles.parameterLabel}>Residual Deviance:</span>
              <span className={styles.parameterValue}>{params.deviance.toFixed(2)} (df={params.degreesOfFreedom})</span>
            </div>
            <div className={styles.parameterItem}>
              <span className={styles.parameterLabel}>AIC:</span>
              <span className={styles.parameterValue}>{params.aic.toFixed(2)}</span>
            </div>
            <div className={styles.parameterItem}>
              <span className={styles.parameterLabel}>BIC:</span>
              <span className={styles.parameterValue}>{params.bic.toFixed(2)}</span>
            </div>
            <div className={styles.parameterItem}>
              <span className={styles.parameterLabel}>Converged:</span>
              <span className={styles.parameterValue}>{params.converged ? 'Yes' : 'No'} ({params.iterations} iterations)</span>
            </div>
          </div>
        </div>
      );
    }

    // 他のモデルタイプの場合
    return (
      <div className={styles.parameterSection}>
        <h3>Model Parameters</h3>
        <pre className={styles.jsonDisplay}>
          {JSON.stringify(model.parameters, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div
      className={styles.container}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>{model.name}</h2>
        <span className={styles.modelType}>{model.type.replace('_', ' ')}</span>
      </div>

      {/* 基本情報セクション */}
      <section className={styles.section}>
        <h3>Basic Information</h3>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Model ID:</span>
            <span className={styles.infoValue}>{model.id}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Type:</span>
            <span className={styles.infoValue}>{model.type.replace('_', ' ')}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Created:</span>
            <span className={styles.infoValue}>{formatDate(model.metadata.createdAt)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Training Dataset:</span>
            <span className={styles.infoValue}>{datasetName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Sample Size:</span>
            <span className={styles.infoValue}>{model.metadata.sampleSize}</span>
          </div>
        </div>
      </section>

      {/* 特徴量と目的変数 */}
      <section className={styles.section}>
        <h3>Features and Target</h3>
        <div className={styles.variablesGrid}>
          <div className={styles.variableSection}>
            <h4>Features (X)</h4>
            <ul className={styles.variableList}>
              {model.metadata.features.map(feature => (
                <li key={feature}>{getColumnName(feature)}</li>
              ))}
            </ul>
          </div>
          <div className={styles.variableSection}>
            <h4>Target (Y)</h4>
            <div className={styles.targetVariable}>{getColumnName(model.metadata.target)}</div>
          </div>
        </div>
      </section>

      {/* モデルパラメータ */}
      {renderParameters()}

      {/* ハイパーパラメータ */}
      {Object.keys(model.hyperparameters).length > 0 && (
        <section className={styles.section}>
          <h3>Hyperparameters</h3>
          <div className={styles.hyperparameters}>
            {Object.entries(model.hyperparameters).map(([key, value]) => (
              <div key={key} className={styles.hyperparameterItem}>
                <span className={styles.hyperparameterKey}>{key}:</span>
                <span className={styles.hyperparameterValue}>
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* アクションボタン */}
      <section className={styles.actionSection}>
        {/* GLMモデルの場合のみDiagnosticsボタンとPredictionボタンを表示 */}
        {model.type === 'glm' && (
          <>
            <button
              className={styles.primaryButton}
              onClick={openDiagnostics}
              aria-label="View GLM diagnostic plots"
            >
              View Diagnostics
            </button>
            <button
              className={styles.primaryButton}
              onClick={openPrediction}
              aria-label="Make predictions using this model"
            >
              Make Predictions
            </button>
          </>
        )}
        <button
          className={styles.secondaryButton}
          disabled
          aria-label="Evaluate model performance (feature coming soon)"
        >
          Evaluate Model (Coming Soon)
        </button>
      </section>
    </div>
  );
};